import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";
import Images from "assets/images";
import Card from "components/visitCommon/Card";
import StepScreenContainer from "components/visitCommon/StepScreenContainer";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import { Icon } from "stories/Icons";
import useVisitStepChange from "hooks/useVisitStepChange";
import { useApiMutation } from "hooks/useApiMutation";
import { updateReferralSourceAction } from "api/actions/UserActions";
import { useCurrentUser } from "hooks/useCurrentUser";
import { setUserDetails } from "reducers/currentUserReducer";
import { cosmetic_steps, referral_source_code } from "helpers/enum";
import { cosmeticConsultationReducers } from "reducers/cosmetic/cosmeticConsultationReducer";
import { useCosmeticConsultationFlow } from "hooks/useConsultationFlow";

const referralOptions = [
  {
    label: "Google",
    value: "google",
    renderIcon: () => <Icon name="google" className="w-10 h-10" />,
  },
  {
    label: "Instagram",
    value: "instagram",
    renderIcon: () => <img src={Images.InstagramIcon} alt="Instagram" />,
  },
  {
    label: "Facebook",
    value: "facebook",
    renderIcon: () => <img src={Images.FacebookIcon} alt="Facebook" />,
  },
  {
    label: "TikTok",
    value: "tiktok",
    renderIcon: () => <img src={Images.TikTokIcon} alt="TikTok" />,
  },
  {
    label: "Doctor's Referral",
    value: "doctor_referral",
    renderIcon: () => <img src={Images.DoctorReferralIcon} alt="Doctor Referral" />,
  },
  {
    label: "Friend/Family",
    value: "friend_or_family",
    renderIcon: () => <img src={Images.FriendFamilyIcon} alt="Friend/Family" />,
  },
  {
    label: "Other",
    value: "other",
    renderIcon: () => <img src={Images.OthersIcon} alt="Other" />,
  },
];

function CosmeticReferralSource() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { userDetails } = useCurrentUser();
  const { incompleteData } = useCosmeticConsultationFlow();
  const [selectedSource, setSelectedSource] = useState(null);
  const [otherText, setOtherText] = useState('');
  const { rightButtonClickRef } = useVisitStepChange();
  const { mutate: callUpdateReferralSource } = useApiMutation(
    updateReferralSourceAction
  );

  useEffect(() => {
    const rawReferralSource = userDetails?.referral_source;
    const rawReferralSourceOtherText = userDetails?.referral_source_other;
    if (referralOptions.some((option) => option.value === rawReferralSource)) {
      setSelectedSource(rawReferralSource);
      setOtherText(rawReferralSourceOtherText || '');
    }
  }, [userDetails?.referral_source, userDetails?.referral_source_other]);

  useEffect(() => {
    const isValid = selectedSource !== "other" || otherText.trim() !== "";
    dispatch(
      cosmeticConsultationReducers.referral_source({
        isValid,
      })
    );
  }, [dispatch, selectedSource, otherText]);

  useEffect(() => {
    const shouldShowReferralStep =
      userDetails?.referral_source == null ||
      userDetails?.referral_source_saved_type === "temporary";
    
    if (!shouldShowReferralStep) {
      dispatch(
        cosmeticConsultationReducers.currentScreen({
          value: cosmetic_steps.previous_treatment,
        })
      );
      history.replace(`${url}/terms_and_conditions`);
    }
  }, [
    dispatch,
    history,
    url,
    userDetails?.referral_source,
    userDetails?.referral_source_saved_type,
  ]);

  function completeReferralStep(referralValue = null) {
    dispatch(
      cosmeticConsultationReducers.referral_source({
        value: referralValue,
      })
    );
    history.push(`${url}/terms_and_conditions`);
  }

  function onRightButtonClick() {
    if (!selectedSource) {
      completeReferralStep(null);
      return;
    }

    callUpdateReferralSource({
      referral_source: referral_source_code[selectedSource],
      referral_source_visit_id: incompleteData?.id ?? null,
      ...(selectedSource === 'other' && {referral_source_other: otherText.trim()})
    }).then((response) => {
      if (response?.error !== true) {
        dispatch(
          setUserDetails({
            userDetails: {
              ...userDetails,
              referral_source: selectedSource,
              referral_source_other: otherText.trim()
            },
          })
        );
        completeReferralStep(selectedSource);
      }
    });
  }

  rightButtonClickRef.current = onRightButtonClick;

  return (
    <StepScreenContainer align="start">
      <MedicalAssistant content={<p>How did you hear about us? (Optional)</p>} />
      <Card>
        <div className="space-y-6">
          {referralOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSelectedSource(option.value)}
              className={`w-full p-6 border rounded-xl flex items-center justify-between ${
                selectedSource === option.value ? "border-orange" : "border-borderColor"
              }`}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 flex items-center justify-center">
                  {option.renderIcon ? (
                    option.renderIcon()
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-indigo bg-opacity-10" />
                  )}
                </div>
                <p className="pl-6 text-2xl font-medium text-indigo">{option.label}</p>
              </div>
              <img
                className="w-10 h-10"
                src={
                  selectedSource === option.value
                    ? Images.radioChecked
                    : Images.uncheckedCircle
                }
                alt="radio"
              />
            </button>
          ))}
        </div>
        {selectedSource === "other" && <div className="mt-6">
          <input type='text' 
            placeholder="Please specify other sources if any (Max 200 chars)" 
            className="w-full p-6 border-b font-medium text-2xl outline-black outline-1"
            value={otherText} onChange={(e) => setOtherText(e.target.value)}
            maxLength={200}
          />
        </div>}
      </Card>
    </StepScreenContainer>
  );
}

export default CosmeticReferralSource;
